import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../css/style.css";
import "../css/mobile.css";
import logo from "../assets/image/rccglogo.webp";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) navigate("/");
  }, [navigate]);

  const [form, setForm] = useState({
    email: "",
    password: "",
    showPassword: false,
  });

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    setErrors({});

    const fieldErrors = {};
    if (!form.email) fieldErrors.email = "Email is required.";
    if (!form.password) fieldErrors.password = "Password is required.";

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      Object.values(fieldErrors).forEach((msg) =>
        toast.error(msg, { theme: "colored", autoClose: 4000 })
      );
      return;
    }

    setLoading(true);

    try {
      const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

      const res = await fetch(`${TEST_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
        }),
      });
      if (res.status === 403) {
        setLoading(false);
        toast.error("Please verify email before proceeding", {
          theme: "colored",
        });
        setTimeout(() => navigate("/verify-email"), 1500);
        return;
      }
      let data = {};
      try {
        data = await res.json();
      } catch (err) {
        data = {};
      }
      if (res.status === 401) {
        setLoading(false);
        toast.error(data.message || "Incorrect email or password.", {
          theme: "colored",
          autoClose: 4000,
        });
        return;
      }

      setLoading(false);
      toast.success("Login successful!", { theme: "colored" });

      if (data.data?.token) {
        localStorage.setItem("authToken", data.data.token);
      }

      if (data.data?.user) {
        localStorage.setItem("user", JSON.stringify(data.data.user));
        localStorage.setItem(
          "userName",
          data.data.user.name || data.data.user.email
        );
        window.dispatchEvent(new Event("userUpdated"));
      }

      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      setLoading(false);
      console.error("Catch block error:", err);
      toast.error("Network error — please try again.", {
        theme: "colored",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  }

  const requiredAsterisk = <span style={{ color: "red" }}> *</span>;

  return (
    <section className="login">
      <div className="containerfluid">
        <div className="row loginrow">
          <div className="col-md-6">
            <div className="loginbg">
              <img src={logo} alt="RCCG Logo" />
              <h5 className="py-5">
                Welcome to RCCG New Beginning Chapel, Bournemouth, UK
              </h5>
              <p className="text-white">
                RCCG Bournemouth Membership Portal has been built to create a
                community where the church members can interact with each other
                seamlessly.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <form className="card" onSubmit={handleSubmit}>
              <h5>Login</h5>

              <label htmlFor="email">Email{requiredAsterisk}</label>
              <input
                id="email"
                name="email"
                value={form.email}
                onChange={onChange}
                type="email"
                placeholder="Enter your email"
                className={`form-control ${
                  errors.email ? "border-danger" : ""
                }`}
              />

              <label htmlFor="password">Password{requiredAsterisk}</label>
              <div className="position-relative">
                <input
                  id="password"
                  name="password"
                  value={form.password}
                  onChange={onChange}
                  type={form.showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  className={`form-control ${
                    errors.password ? "border-danger" : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      showPassword: !prev.showPassword,
                    }))
                  }
                  className="eye-icon"
                  style={{ cursor: "pointer" }}
                >
                  {form.showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <a href="/forgot-password">Forgot Password?</a>
              <button
                type="submit"
                disabled={loading}
                style={{ fontSize: "14px" }}
              >
                {loading ? "Logging in..." : "Login"}
              </button>

              <p>
                Not yet a member?{" "}
                <a href="/signup" className="text-primary">
                  Register Now
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
